import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ApiService } from 'src/app/helpers/services/api.service';
import { HttpService } from 'src/app/helpers/services/http.service';
import { ToastresService } from 'src/app/helpers/services/toaster.service';

@Component({
  selector: 'app-role-permission-form',
  templateUrl: './role-permission-form.component.html',
  styleUrls: ['./role-permission-form.component.css'],
})
export class RolePermissionFormComponent {
  public mode: 'role' | 'user' = 'role';
  public role?: any;
  public userId?: number;

  permissions: any[] = [];
  selectedPermissionIds: number[] = [];
  form: FormGroup;
  submitted: boolean = false;
  codeToIdMap: Record<string, number> = {};
  radioPermissionCodes: string[] = [];
  radioPriorityGroups: string[][] = [
    ['VIEW_ALL_USERS', 'VIEW_MY_USERS'],
    ['VIEW_ALL_CLIENTS', 'VIEW_MY_CLIENTS'],
    ['VIEW_ALL_TEAMS', 'VIEW_MY_TEAMS'],
    ['VIEW_ALL_WORKSPACES', 'VIEW_MY_WORKSPACES'],
    ['VIEW_ALL_PROJECTS', 'VIEW_MY_PROJECTS'],
    ['VIEW_ALL_STORIES', 'VIEW_MY_STORIES'],
    ['VIEW_ALL_TASKS', 'VIEW_MY_TASKS'],
    ['VIEW_ALL_MEETINGS', 'VIEW_MY_MEETINGS'],
    [
      'VIEW_QUOTES_AS_ADMIN',
      'VIEW_QUOTES_AS_USER',
      'VIEW_QUOTES_AS_LEADER',
      'VIEW_QUOTES_AS_CLIENT',
    ],
  ];

  constructor(
    public activeModal: NgbActiveModal,
    public httpService: HttpService,
    private api: ApiService,
    private toastrsService: ToastresService,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.refreshRadioCodesFromGroups();
    this.getPermissions();
  }

  private refreshRadioCodesFromGroups() {
    const set = new Set<string>();
    this.radioPriorityGroups.forEach((g) => g.forEach((code) => set.add(code)));
    this.radioPermissionCodes = Array.from(set);
  }

  get f() {
    return this.form.controls;
  }

  getPermissions() {
    this.httpService
      .list(this.api.common.permissions, {}, 'permissionsLoader')
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.permissions = res.data;
            this.buildCodeToIdMap();
            this.getRolePermissions();
          }
        },
      });
  }

  getRolePermissions() {
    if (this.mode === 'user') {
      this.httpService
        .list(
          this.api.users.permissions(this.userId!),
          {},
          'rolePermissionsLoader',
        )
        .subscribe({
          next: (res: any) => {
            if (res.success) {
              res.data.forEach((permission: any) => {
                this.selectPermissionRecursively(permission, true);
              });
              this.normalizeSelectedRadios();
            }
          },
        });
    } else {
      if (!this.role) {
        this.permissions.forEach((permission: any) => {
          this.selectPermissionRecursively(permission, true);
        });
        this.normalizeSelectedRadios();
      } else {
        this.httpService
          .list(
            this.api.roles.details(this.role.id),
            {},
            'rolePermissionsLoader',
          )
          .subscribe({
            next: (res: any) => {
              if (res.success) {
                res.data.permissions.forEach((permission: any) => {
                  this.selectPermissionRecursively(permission, true);
                });
                this.normalizeSelectedRadios();
              }
            },
          });
      }
    }
  }

  save(): void {
    this.submitted = true;

    if (this.mode === 'user') {
      const payload = { allowed_permissions: this.selectedPermissionIds };

      this.httpService
        .action(
          this.api.users.customizePermissions(this.userId!),
          payload,
          'actionsRoleLoader',
        )
        .subscribe({
          next: (res: any) => {
            if (res.success) {
              this.activeModal.close();
              this.toastrsService.ShowSuccess(res.msg);
            } else {
              this.toastrsService.ShowError(res.msg);
            }
          },
        });
      return;
    }

    if (this.form.valid) {
      const payload = {
        name: this.f.Name.value,
        permissions_ids: this.selectedPermissionIds,
      };

      const apiUrl = this.role
        ? this.api.roles.edit(this.role.id)
        : this.api.roles.add;
      this.httpService.action(apiUrl, payload, 'actionsRoleLoader').subscribe({
        next: (res: any) => {
          if (res.success) {
            this.activeModal.close();
            this.toastrsService.ShowSuccess(res.msg);
          } else {
            this.toastrsService.ShowError(res.msg);
          }
        },
      });
    }
  }

  isSelected(id: number): boolean {
    return this.selectedPermissionIds.includes(id);
  }

  onSelect(id: number, event: any) {
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.isSelected(id)) this.selectedPermissionIds.push(id);
    } else {
      const index = this.selectedPermissionIds.indexOf(id);
      if (index !== -1) this.selectedPermissionIds.splice(index, 1);
    }
    this.normalizeSelectedRadios();
  }

  hasAnyPermission(permissions: any[], code: string): boolean {
    return permissions.some((permission) => permission.code === code);
  }

  getPermissionId(permissions: any[], code: string): number {
    const permission = permissions.find(
      (permission) => permission.code === code,
    );
    return permission ? permission.id : -1;
  }

  toggleGroupPermissions(permissionGroup: any, event: any) {
    const isChecked = event.target.checked;
    permissionGroup.permissions.forEach((permission: any) => {
      const id = permission.id;
      if (isChecked && !this.isSelected(id)) {
        this.selectedPermissionIds.push(id);
      } else if (!isChecked && this.isSelected(id)) {
        const index = this.selectedPermissionIds.indexOf(id);
        if (index !== -1) this.selectedPermissionIds.splice(index, 1);
      }
    });
    this.normalizeSelectedRadios();
  }

  areAllGroupPermissionsSelected(permissions: any[]): boolean {
    return permissions.every((permission) => this.isSelected(permission.id));
  }

  togglePermission(permission: any, event: any) {
    const isChecked = event.target.checked;

    if (permission.sub_permissions) {
      this.updateChildrenPermissions(permission, isChecked);
    } else {
      this.updatePermissionSelection(permission, isChecked);
    }

    this.updateParentPermissionState(permission);
    this.normalizeSelectedRadios();
  }

  updateChildrenPermissions(parentPermission: any, isChecked: boolean) {
    parentPermission.sub_permissions.forEach((subPermission: any) => {
      this.updatePermissionSelection(subPermission, isChecked);
    });
    this.updatePermissionSelection(parentPermission, isChecked);
  }

  updatePermissionSelection(permission: any, isChecked: boolean) {
    const index = this.selectedPermissionIds.indexOf(permission.id);
    if (isChecked && index === -1) {
      this.selectedPermissionIds.push(permission.id);
    } else if (!isChecked && index !== -1) {
      this.selectedPermissionIds.splice(index, 1);
    }
  }

  updateParentPermissionState(permission: any) {
    const parentPermission = this.permissions.find(
      (p: any) =>
        p.sub_permissions &&
        p.sub_permissions.some((sp: any) => sp.id === permission.id),
    );
    if (parentPermission) {
      const anySubPermissionSelected = parentPermission.sub_permissions.some(
        (sp: any) => this.selectedPermissionIds.includes(sp.id),
      );
      const parentIndex = this.selectedPermissionIds.indexOf(
        parentPermission.id,
      );

      if (anySubPermissionSelected && parentIndex === -1) {
        this.selectedPermissionIds.push(parentPermission.id);
      } else if (!anySubPermissionSelected && parentIndex !== -1) {
        this.selectedPermissionIds.splice(parentIndex, 1);
      }
    }
  }

  selectPermissionRecursively(permission: any, isChecked: boolean) {
    this.updatePermissionSelection(permission, isChecked);
    if (permission.sub_permissions) {
      permission.sub_permissions.forEach((subPermission: any) => {
        this.selectPermissionRecursively(subPermission, isChecked);
      });
    }
  }

  onPermissionChange(parent: any, permission: any, event: any) {
    const isRadio = this.radioPermissionCodes.includes(permission.code);
    const isChecked = event.target.checked;

    if (isRadio) {
      parent.sub_permissions.forEach((sub: any) => {
        if (this.radioPermissionCodes.includes(sub.code)) {
          const index = this.selectedPermissionIds.indexOf(sub.id);
          if (index !== -1) this.selectedPermissionIds.splice(index, 1);
        }
      });

      if (isChecked) this.selectedPermissionIds.push(permission.id);

      this.normalizeSelectedRadios();
    } else {
      this.togglePermission(permission, event);
    }

    this.updateParentPermissionState(permission);
  }

  buildCodeToIdMap() {
    this.codeToIdMap = {};

    const visit = (perm: any) => {
      if (perm && typeof perm.id === 'number' && perm.code) {
        this.codeToIdMap[perm.code] = perm.id;
      }
      if (Array.isArray(perm?.sub_permissions)) {
        perm.sub_permissions.forEach(visit);
      }
    };

    const visitAny = (node: any) => {
      if (!node) return;
      if (Array.isArray(node.permissions)) {
        node.permissions.forEach(visitAny);
      }
      if (Array.isArray(node.sub_permissions)) {
        node.sub_permissions.forEach(visitAny);
      }
      visit(node);
    };

    this.permissions.forEach(visitAny);
  }

  normalizeSelectedRadios() {
    if (!this.codeToIdMap || Object.keys(this.codeToIdMap).length === 0) {
      this.buildCodeToIdMap();
    }

    this.radioPriorityGroups.forEach((group) => {
      const ids = group
        .map((code) => this.codeToIdMap[code])
        .filter((id) => typeof id === 'number');

      const present = ids.filter((id) =>
        this.selectedPermissionIds.includes(id),
      );
      if (present.length <= 1) return;

      const strongest = ids[0];
      if (!this.selectedPermissionIds.includes(strongest)) {
        this.selectedPermissionIds.push(strongest);
      }
      ids.slice(1).forEach((weakId) => {
        const idx = this.selectedPermissionIds.indexOf(weakId);
        if (idx !== -1) this.selectedPermissionIds.splice(idx, 1);
      });
    });
  }

  initForm() {
    this.form = new FormGroup({
      Name: new FormControl(
        this.role ? this.role.name : '',
        Validators.required,
      ),
    });
  }
}
